// filepath: apps/web/src/admin/people/api.ts
//
// ISS-518 — typed REST client for /api/people/*.
//
// Wire shape per `internal/directory/handlers.go:158-172` (6 mounted
// routes) + `internal/directory/person.go:57-126` (Person struct).
//
// BA ruling on ISS-518 (note 18233 verbatim):
//   "Value blob: unwrap at the API client; SPA types stay flat.
//    Server-stripped fields (FR-AUTH-7 invariant): every field
//    from the value blob must be OPTIONAL in the SPA type."
//
// THIS MODULE
//   - Routes every call through `apiFetch` (bearer + 401 retry
//     chain).
//   - Unwraps the Go `value` blob (`internal/directory/person.go:85`)
//     into the SPA's flat Person shape. The SPA's typed
//     `Person` interface (`apps/web/src/features/directory/types.ts#Person`)
//     is the post-unwrap surface — downstream consumers never
//     touch `.value.*`.
//   - Forwards INV-8 `X-Idempotency-Key` on every mutation via
//     `newIdempotencyKey()`.
//
// INV-2: the unwrap never synthesises display fields; if the
// server strip pass dropped them, the SPA reads `undefined`
// (per the BA + FR-AUTH-7 server-stripped OPTIONAL rule).
//
// INV-7: every mutation carries `row_version` (OCC) and the
// server returns 409 + code='row_version_mismatch' on stale
// submission.

import { apiFetch } from "@/api";
import { ApiError } from "@/definitions/types";
import type { Person } from "@/features/directory/types";

/** Wire shape of a Person record as Go emits it. */
interface PersonWire {
  id: string;
  tenant_id: string;
  definition_version: number;
  value: Record<string, unknown>;
  row_version: number;
  created_at: string;
  updated_at: string;
}

/** Wire envelope for the list + audit endpoints. */
interface RecordsEnvelope<T> {
  records: T[];
  total?: number;
}

/** Input for `createPerson` (no id / row_version / audit fields). */
export interface CreatePersonInput {
  /** Per BA: the only required typed column. The form layer enforces. */
  display_name: string;
  /** FR-AUTH-7 server-stripped OPTIONAL. Form may omit when blank. */
  email?: string | null;
  phone?: string | null;
  /** "active" | "inactive" | "archived" per the Go typed column. */
  status?: "active" | "inactive" | "archived";
}

/** Input for `updatePerson` — INV-7 row_version is mandatory. */
export interface UpdatePersonInput {
  display_name?: string;
  email?: string | null;
  phone?: string | null;
  status?: "active" | "inactive" | "archived";
  row_version: number;
}

/** Query opts for `listPeople`. */
export interface ListPeopleOpts {
  limit?: number;
  offset?: number;
}

/**
 * Generate a v4 UUID via crypto.randomUUID(). Mirrors the
 * counterparties/locations API client idiom.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function callApi<T>(
  url: string,
  opts: {
    method?: "GET" | "POST" | "PATCH" | "DELETE";
    body?: unknown;
    idempotencyKey?: string;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const resp = await apiFetch(url, {
    method: opts.method ?? "GET",
    init,
    idempotencyKey: opts.idempotencyKey,
  });
  if (!resp.ok) {
    let code = "";
    let message = `HTTP ${resp.status}`;
    try {
      const errBody = (await resp.json()) as {
        code?: string;
        message?: string;
        error?: string;
      };
      if (typeof errBody.code === "string") code = errBody.code;
      if (typeof errBody.message === "string") message = errBody.message;
      else if (typeof errBody.error === "string") message = errBody.error;
    } catch {
      /* not JSON */
    }
    throw new ApiError(message, resp.status, code);
  }
  if (resp.status === 204) {
    return undefined as unknown as T;
  }
  return (await resp.json()) as T;
}

/** The 6 form fields the BA spec names as the Slice 1 form's
 *  source of truth (from the GO `value` typed columns).
 *  legal_name / date_of_birth / tax_id / locale are out of scope
 *  for ISS-518 per the BA ruling. */
const SLICE1_FORM_FIELDS = [
  "display_name",
  "email",
  "phone",
  "status",
] as const;

function pickFields(
  input: CreatePersonInput | UpdatePersonInput,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of SLICE1_FORM_FIELDS) {
    const v = (input as unknown as Record<string, unknown>)[k];
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * Unwrap the Go `value` blob into the flat SPA Person shape.
 * Per the BA + FR-AUTH-7 invariant: every `value.*` field is
 * OPTIONAL on the SPA side — an absent key is mapped to
 * `undefined`, NOT synthesised.
 */
function unwirePerson(raw: PersonWire): Person {
  const value = raw.value ?? {};
  return {
    id: raw.id,
    tenant_id: raw.tenant_id,
    definition_version: raw.definition_version ?? 0,
    display_name: (value["display_name"] as string | undefined) ?? undefined,
    legal_name: (value["legal_name"] as string | null | undefined) ?? null,
    date_of_birth:
      (value["date_of_birth"] as string | null | undefined) ?? null,
    tax_id: (value["tax_id"] as string | null | undefined) ?? null,
    email: (value["email"] as string | null | undefined) ?? null,
    phone: (value["phone"] as string | null | undefined) ?? null,
    locale: (value["locale"] as string | null | undefined) ?? null,
    status: (value["status"] as Person["status"] | undefined) ?? undefined,
    keycloak_subject:
      (value["keycloak_subject_id"] as string | null | undefined) ?? null,
    row_version: raw.row_version,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

const BASE = "/api/people";

/**
 * GET /api/people?... — `{records, total}` envelope.
 * Per Go handler at `internal/directory/handlers.go:313-330`.
 */
export async function listPeople(opts?: ListPeopleOpts): Promise<Person[]> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
  const qs = params.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  const body = (await callApi<unknown>(url)) as
    RecordsEnvelope<PersonWire> | PersonWire[];
  if (Array.isArray(body)) return body.map(unwirePerson);
  if (Array.isArray(body?.records)) return body.records.map(unwirePerson);
  return [];
}

/**
 * GET /api/people/{id}
 * Per Go handler at handlers.go:358 ({id} is path-segment, NOT ?id=).
 */
export async function getPerson(id: string): Promise<Person> {
  const raw = await callApi<PersonWire>(`${BASE}/${encodeURIComponent(id)}`);
  return unwirePerson(raw);
}

/**
 * POST /api/people — body `{fields: {display_name, email, phone, status}}`.
 * Returns the freshly-created Person (row_version=1 from server).
 */
export async function createPerson(
  input: CreatePersonInput,
  idempotencyKey: string,
): Promise<Person> {
  const raw = await callApi<PersonWire>(BASE, {
    method: "POST",
    body: { fields: pickFields(input) },
    idempotencyKey,
  });
  return unwirePerson(raw);
}

/**
 * PATCH /api/people/{id} — body `{fields, row_version}` per Go handler
 * at handlers.go:385. Returns 200 + the updated row, or 409 +
 * code='row_version_mismatch' on stale submission (INV-7).
 */
export async function updatePerson(
  id: string,
  input: UpdatePersonInput,
  idempotencyKey: string,
): Promise<Person> {
  const raw = await callApi<PersonWire>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: {
      fields: pickFields(input),
      row_version: input.row_version,
    },
    idempotencyKey,
  });
  return unwirePerson(raw);
}

/**
 * DELETE /api/people/{id} — soft-delete (Go handler at
 * handlers.go:417 sets status='inactive'; the row stays in
 * tenant schema for audit history). Returns 204. Inv-7: the
 * server demands row_version in the body.
 */
export async function archivePerson(
  id: string,
  expectedRowVersion: number,
  idempotencyKey: string,
): Promise<void> {
  await callApi<void>(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: { row_version: expectedRowVersion },
    idempotencyKey,
  });
}
